import { useEffect } from 'react'
import {
  getIndicator,
  getCountries,
  getCitiesDepualc,
  getDamDepualc,
  MAP_ID
} from './apiHelper'
import { rangeColors, rangeSize } from './configQueries'
import { useMapEngine } from '../components/shared'

import { useLayer } from '@facility-latam/geoportal-components'

export const getIndicatorParams = async ({
  indicatorId,
  members,
  isCountry,
  dimensionType
}) => {
  const dataFetch = getIndicator({ id: indicatorId, members })
  const countriesFetch = getCountries()
  const citiesDepualcFetch = getCitiesDepualc()
  const damDepualcFetch = getDamDepualc()

  const getStepStops = (range) => {
    const [min, max] = [
      Math.floor(range[0]),
      Math.ceil(range[range.length - 1])
    ]
    const stepValue = (max - min + 1) / 6
    const stepStops = [
      min,
      min + Math.ceil(stepValue),
      min + Math.ceil(stepValue * 2),
      min + Math.ceil(stepValue * 3),
      min + Math.ceil(stepValue * 4),
      min + Math.ceil(stepValue * 5),
      max + 1
    ]

    return stepStops
  }

  const [{ metadata, data }, { countries }, { citiesDepualc }, { damDepualc }] =
    await Promise.all([
      dataFetch,
      countriesFetch,
      citiesDepualcFetch,
      damDepualcFetch
    ])

  const existList = []
  const getProcessParams = (isCountry, dimensionType) => {
    const region = isCountry
      ? 'COUNTRIES'
      : dimensionType.includes('cities_depualc')
      ? 'CITIES'
      : 'DEPARTMENTS'

    const regiones = {
      COUNTRIES: {
        base: countries,
        filterData: ({ dim_208: id }) =>
          base.features.find(({ properties: { gc_id: gcId } }) => gcId === id),
        findProperty: (properties, { dim_208: dimCountry }) =>
          properties.gc_id === dimCountry
      },
      DEPARTMENTS: {
        base: damDepualc,
        filterData: ({ dim_79515 }) => {
          const exist = base.features.find(
            ({ properties: { id_cepalstat } }) =>
              dim_79515.toString() === id_cepalstat
          )
          if (exist && !existList.includes(dim_79515)) {
            existList.push(dim_79515)
            return exist
          }
          return false
        },
        findProperty: (properties, { dim_79515 }) =>
          properties.id_cepalstat === dim_79515.toString()
      },
      CITIES: {
        base: citiesDepualc,
        filterData: ({ dim_79515 }) =>
          base.features.find(
            ({ properties: { id } }) => dim_79515.toString() === id
          ),
        findProperty: (properties, { dim_79515 }) =>
          properties.id === dim_79515.toString()
      }
    }

    return regiones[region]
  }

  const { base, filterData, findProperty } = getProcessParams(
    isCountry,
    dimensionType
  )

  const range = data
    .filter(filterData)
    .map(({ value }) => parseFloat(value))
    .sort((a, b) => a - b)

  base.features = base.features
    .map(({ id, properties, ...others }, idx) => ({
      id: id ? id : idx,
      properties: {
        ...properties,
        values: Math.floor(
          data.find((d) => findProperty(properties, d))?.value ?? 0.0
        )
      },
      ...others
    }))
    .filter(({ properties: { values } }) => values > 0)

  const stepStops = getStepStops(range)
  const stepsAux = stepStops.flatMap((value, idx) => [
    rangeColors[4][idx],
    value
  ])
  const stepsColor = stepStops.flatMap((value, idx) => [rangeSize[idx], value])

  return {
    geojson: base,
    steps: stepsAux,
    stepsColor,
    metadata
  }
}

export const useLayerIndicator = ({ newIndicator }) => {
  const { id, type, paint, dataIndicator, steps, stepsColor } = newIndicator
  const [engine] = useMapEngine(MAP_ID)

  const [setLayerData] = useLayer({
    engine,
    layer: {
      id,
      type,
      layout: {
        visibility: 'visible'
      },
      paint
    }
  })

  useEffect(() => {
    if (engine && dataIndicator && setLayerData) {
      setLayerData(dataIndicator)
    }
  }, [engine, dataIndicator, setLayerData])

  useEffect(() => {
    if (engine && steps) {
      engine.setPaintProperty(
        id,
        `${type === 'circle' ? 'circle-color' : 'fill-color'}`,
        ['step', ['get', 'values'], ...steps, '#36393f']
      )
    }
  }, [engine, steps, id, type])

  useEffect(() => {
    if (engine && stepsColor && type === 'circle') {
      engine.setPaintProperty(id, 'circle-radius', [
        'step',
        ['get', 'values'],
        ...stepsColor,
        18
      ])
    }
  }, [engine, id, stepsColor, type])

  return [id]
}

export const getPaintIndicator = new Map([
  [
    'circle',
    {
      'circle-color': '#36393f',
      'circle-radius': 8,
      'circle-stroke-color': '#36393f',
      'circle-stroke-opacity': 0.9,
      'circle-stroke-width': 0.5
    }
  ],
  ['fill', { 'fill-outline-color': 'white' }]
])
